import type {
    AddressMatches,
    AddressMatchResult,
    AddressMatchType,
} from 'features/my-zone/support-requests/components/support-requests-modals/types';
import { normalizeString } from 'features/my-zone/support-requests/components/support-requests-modals/utils/utils';
import {
    nameMatches,
    addressMatches,
    zipCodeMatches,
} from 'features/my-zone/support-requests/components/support-requests-modals/consts/consts';
import type { ParsedDataRow } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { validateColumn } from 'features/my-zone/support-requests/components/support-requests-modals/utils/columns-validation/columns-validation';

const findExactMatch = (columnName: string, matches: string[]) => {
    const normalizedColumn = normalizeString(columnName);
    return matches.some((match) => normalizeString(match) === normalizedColumn)
        ? columnName
        : undefined;
};

const findContainsMatch = (
    columnName: string,
    keywords: string[],
    exactOnlyMatches?: Set<string>,
) => {
    const normalizedColumn = normalizeString(columnName);
    const filteredKeywords = exactOnlyMatches
        ? keywords.filter((keyword) => !exactOnlyMatches.has(keyword))
        : keywords;
    return filteredKeywords.some((keyword) => normalizedColumn.includes(normalizeString(keyword)))
        ? columnName
        : undefined;
};

export const detectColumnType = (column: string) => {
    const fieldPatterns = {
        zip_code: zipCodeMatches.exactMatches,
        lat: addressMatches.latMatches,
        lng: addressMatches.lngMatches,
    };

    for (const [type, matches] of Object.entries(fieldPatterns)) {
        if (findExactMatch(column, matches)) {
            return type;
        }
    }

    for (const [type, matches] of Object.entries(fieldPatterns)) {
        if (findContainsMatch(column, matches, addressMatches.exactOnlyMatches)) {
            return type;
        }
    }

    return undefined;
};

export const findNameMatch = (columns: string[]) => {
    const { exactMatches, keywords } = nameMatches;

    for (const column of columns) {
        const matchedColumn = findExactMatch(column, exactMatches);
        if (matchedColumn) {
            return matchedColumn;
        }
    }

    for (const column of columns) {
        const matchedColumn = findContainsMatch(column, keywords);
        if (matchedColumn) {
            return matchedColumn;
        }
    }

    return undefined;
};

export const findAddressMatches = (
    columns: string[],
    data: ParsedDataRow[],
): AddressMatchResult | undefined => {
    const possibleMatches: Record<keyof AddressMatches, string | undefined> = {
        lat: undefined,
        lng: undefined,
        street: undefined,
        city: undefined,
        state: undefined,
        formatted_address: undefined,
    };

    const {
        latMatches,
        lngMatches,
        streetMatches,
        cityMatches,
        stateMatches,
        fullAddressMatches,
        exactOnlyMatches,
    } = addressMatches;

    const fieldConfig: Record<
        keyof AddressMatches,
        {
            matches: string[];
            needsValidation?: boolean;
            exactOnlyMatchSet?: Set<string>;
        }
    > = {
        lat: { matches: latMatches, needsValidation: true },
        lng: { matches: lngMatches, needsValidation: true },
        street: { matches: streetMatches },
        city: { matches: cityMatches },
        state: { matches: stateMatches },
        formatted_address: { matches: fullAddressMatches },
    };

    const tryMatchField = (
        column: string,
        fieldKey: keyof AddressMatches,
        matchFn: (col: string, matches: string[], exact?: Set<string>) => string | undefined,
    ) => {
        if (possibleMatches[fieldKey]) return;

        const config = fieldConfig[fieldKey];
        const match = matchFn(column, config.matches, exactOnlyMatches);

        if (!match) return;

        if (config.needsValidation && !validateColumn(match, data).isValid) return;

        possibleMatches[fieldKey] = match;
    };

    for (const column of columns) {
        (Object.keys(fieldConfig) as Array<keyof AddressMatches>).forEach((key) => {
            tryMatchField(column, key, findExactMatch);
        });
    }

    for (const column of columns) {
        (Object.keys(fieldConfig) as Array<keyof AddressMatches>).forEach((key) => {
            tryMatchField(column, key, findContainsMatch);
        });
    }

    const typeDetectors: Array<{ type: AddressMatchType; detect: () => boolean }> = [
        {
            type: 'lat_lng',
            detect: () => !!(possibleMatches.lat && possibleMatches.lng),
        },
        {
            type: 'separate_columns',
            detect: () =>
                !!(possibleMatches.street && possibleMatches.city && possibleMatches.state),
        },

        {
            type: 'lat_lng',
            detect: () => !!(possibleMatches.lat || possibleMatches.lng),
        },
        {
            type: 'separate_columns',
            detect: () =>
                !!(possibleMatches.street || possibleMatches.city || possibleMatches.state),
        },
        {
            type: 'single_column',
            detect: () => !!possibleMatches.formatted_address,
        },
    ];

    const bestMatch = typeDetectors.find((detector) => detector.detect());
    if (!bestMatch) return undefined;

    const filteredMatches = Object.entries(possibleMatches).reduce((acc, [key, value]) => {
        if (value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, string>);

    return {
        type: bestMatch.type,
        matches: filteredMatches,
    };
};

export const findZipCodeMatch = (columns: string[], data: ParsedDataRow[]) => {
    const { exactMatches, keywords } = zipCodeMatches;

    for (const column of columns) {
        const matchedColumn = findExactMatch(column, exactMatches);
        if (matchedColumn && validateColumn(matchedColumn, data).isValid) {
            return matchedColumn;
        }
    }

    for (const column of columns) {
        const matchedColumn = findContainsMatch(column, keywords);
        if (matchedColumn && validateColumn(matchedColumn, data).isValid) {
            return matchedColumn;
        }
    }

    return undefined;
};
